import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Renderer, Program, Triangle, Mesh } from 'ogl';
import './TickEtcMinimal.css';
import AnimatedMockup from './AnimatedMockup';
import MobileAnalyticsDashboard from './components/MobileAnalyticsDashboard';
import SlotMachineStrip from './SlotMachineStrip';
import { SeatedShowcase } from './components/SeatedShowcase/SeatedShowcase';
import './components/SeatedShowcase/SeatedShowcase.css';
import JourneyCards from './components/JourneyCards';

/* ── Inline SVG icon set ── */
function PsIcon({ name }) {
  const icons = {
    ticket: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 9a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v1a2 2 0 0 0 0 4v1a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-1a2 2 0 0 0 0-4V9Z" />
        <line x1="9" y1="7" x2="9" y2="17" strokeDasharray="2 2" />
      </svg>
    ),
    payment: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="3" />
        <path d="M2 10h20" />
        <path d="M6 15h4" />
        <circle cx="17" cy="15" r="1.5" fill="currentColor" stroke="none" />
      </svg>
    ),
    seatmap: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 17v-2a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v2" />
        <rect x="6" y="17" width="3" height="3" rx="1" />
        <rect x="15" y="17" width="3" height="3" rx="1" />
        <circle cx="8" cy="7" r="2" />
        <circle cx="16" cy="7" r="2" />
        <path d="M11 9h2" />
      </svg>
    ),
    dashboard: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="8" rx="1.5" />
        <rect x="14" y="3" width="7" height="5" rx="1.5" />
        <rect x="14" y="12" width="7" height="9" rx="1.5" />
        <rect x="3" y="15" width="7" height="6" rx="1.5" />
      </svg>
    ),
  };
  return icons[name] || null;
}

/* ── Per-tab animated visual ── */
function PsAnimation({ tab }) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1200);
    return () => clearInterval(id);
  }, [tab]);

  if (tab === 'ticketing') {
    const phases = ['Early Bird', 'Regular', 'Last Call', 'VIP'];
    const sold = [92, 67, 34, 18];
    return (
      <div className="psa psa--ticketing">
        <div className="psa-label">TICKET PHASES</div>
        {phases.map((p, i) => (
          <div key={p} className="psa-phase-row">
            <span className="psa-phase-name">{p}</span>
            <div className="psa-phase-bar-track">
              <div
                className="psa-phase-bar-fill"
                style={{ width: `${sold[i]}%`, transitionDelay: `${i * 0.15}s` }}
              />
            </div>
            <span className="psa-phase-pct">{sold[(tick + i) % 4 === 0 ? (i + 1) % 4 : i]}%</span>
          </div>
        ))}
        <div className="psa-discount-badge">
          <span>🏷</span> EARLY20 — active
        </div>
      </div>
    );
  }

  if (tab === 'payments') {
    const txns = [
      { name: 'Tomorrowland NL', amount: '+€ 4,280', color: '#10B981' },
      { name: 'Club Shelter', amount: '+€ 890', color: '#10B981' },
      { name: 'PSP Fee', amount: '−€ 42', color: '#EF4444' },
      { name: 'Organizer payout', amount: '+€ 3,840', color: '#10B981' },
    ];
    const active = tick % txns.length;
    return (
      <div className="psa psa--payments">
        <div className="psa-label">LIVE SETTLEMENTS</div>
        <div className="psa-split-visual">
          <div className="psa-split-bar">
            <div className="psa-split-org" style={{ flex: 85 }}>85% Organizer</div>
            <div className="psa-split-fee" style={{ flex: 15 }}>15%</div>
          </div>
        </div>
        <div className="psa-txn-list">
          {txns.map((t, i) => (
            <div key={i} className={`psa-txn-row ${i === active ? 'psa-txn-row--active' : ''}`}>
              <span className="psa-txn-name">{t.name}</span>
              <span className="psa-txn-amount" style={{ color: t.color }}>{t.amount}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (tab === 'seatmaps') {
    const rows = ['A', 'B', 'C', 'D'];
    const seats = [6, 8, 8, 6];
    const vipRows = ['A'];
    const takenMap = {
      A: [1, 3, 5], B: [2, 4, 6, 7], C: [1, 3, 5, 8], D: [2, 5]
    };
    return (
      <div className="psa psa--seatmap">
        <div className="psa-label">INTERACTIVE SEATING</div>
        <div className="psa-stage">STAGE</div>
        <div className="psa-grid">
          {rows.map((row) => (
            <div key={row} className="psa-row">
              <span className="psa-row-label">{row}</span>
              {Array.from({ length: seats[rows.indexOf(row)] }, (_, s) => {
                const seatNum = s + 1;
                const taken = takenMap[row].includes(seatNum);
                const vip = vipRows.includes(row);
                return (
                  <div
                    key={s}
                    className={`psa-seat ${taken ? 'psa-seat--taken' : ''} ${vip ? 'psa-seat--vip' : ''}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
        <div className="psa-legend">
          <span><span className="psa-dot psa-dot--free" />Available</span>
          <span><span className="psa-dot psa-dot--taken" />Sold</span>
          <span><span className="psa-dot psa-dot--vip" />VIP</span>
        </div>
      </div>
    );
  }

  if (tab === 'dashboard') {
    const bars = [40, 65, 55, 80, 70, 90, 75];
    const days = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
    const metrics = [
      { label: 'Revenue', value: '€ 48,290', delta: '+12%' },
      { label: 'Tickets', value: '3,841', delta: '+8%' },
      { label: 'Avg. order', value: '€ 38.20', delta: '+3%' },
      { label: 'Conversion', value: '68%', delta: '+5%' },
    ];
    const activeMetric = tick % metrics.length;
    return (
      <div className="psa psa--dashboard">
        <div className="psa-label">LIVE OVERVIEW</div>
        <div className="psa-metrics">
          {metrics.map((m, i) => (
            <div key={i} className={`psa-metric ${i === activeMetric ? 'psa-metric--active' : ''}`}>
              <div className="psa-metric-label">{m.label}</div>
              <div className="psa-metric-value">{m.value}</div>
              <div className="psa-metric-delta">{m.delta}</div>
            </div>
          ))}
        </div>
        <div className="psa-chart">
          {bars.map((h, i) => (
            <div key={i} className="psa-chart-col">
              <div className="psa-chart-bar" style={{ height: `${h}%`, transitionDelay: `${i * 0.06}s` }} />
              <span className="psa-chart-day">{days[i]}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

function WhySection() {
  const outerRef = useRef(null);
  const stickyRef = useRef(null);
  const lockRef = useRef(false);
  const eligibleRef = useRef(false);
  const touchStartRef = useRef(null);
  const cardProgressRef = useRef(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [cardSequenceProgress, setCardSequenceProgress] = useState(0);
  const [isSequenceLocked, setIsSequenceLocked] = useState(false);
  const REPLAY_END = 1.6;

  const clamp01 = value => Math.max(0, Math.min(1, value));
  const smoothStep = (start, end, value) => {
    if (end <= start) return value >= end ? 1 : 0;
    const t = clamp01((value - start) / (end - start));
    return t * t * (3 - 2 * t);
  };

  useEffect(() => {
    const handleScroll = () => {
      const el = outerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      const progress = total > 0 ? clamp01(-rect.top / total) : 0;
      setScrollProgress(progress);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    cardProgressRef.current = cardSequenceProgress;
  }, [cardSequenceProgress]);

  useEffect(() => {
    const outerEl = outerRef.current;
    const stickyEl = stickyRef.current;
    if (!outerEl || !stickyEl) return undefined;
    const TITLE_LOCK_TOP = 124;
    const ICON_PHASE_DONE = 0.1;

    const syncLockState = nextLocked => {
      lockRef.current = nextLocked;
      setIsSequenceLocked(prev => (prev === nextLocked ? prev : nextLocked));
    };

    const updateEligibility = () => {
      const rect = stickyEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      eligibleRef.current =
        rect.top <= TITLE_LOCK_TOP &&
        rect.bottom >= viewportHeight * 0.72 &&
        scrollProgress >= ICON_PHASE_DONE;

      if (!eligibleRef.current && lockRef.current) {
        syncLockState(false);
      }
    };

    const consumeDelta = deltaY => {
      updateEligibility();
      const current = cardProgressRef.current;

      if (!eligibleRef.current) return false;
      if (deltaY > 0 && current >= REPLAY_END) {
        syncLockState(false);
        return false;
      }
      if (deltaY < 0 && current <= 0) {
        syncLockState(false);
        return false;
      }

      syncLockState(true);

      const next = Math.max(0, Math.min(REPLAY_END, current + deltaY / 950));
      if (next === current) return true;

      cardProgressRef.current = next;
      setCardSequenceProgress(next);
      return true;
    };

    const handleWheel = event => {
      if (!consumeDelta(event.deltaY)) return;
      event.preventDefault();
    };

    const handleKeyDown = event => {
      const forwardKeys = ['ArrowDown', 'PageDown', ' ', 'Spacebar'];
      const backwardKeys = ['ArrowUp', 'PageUp'];

      if (forwardKeys.includes(event.key)) {
        if (!consumeDelta(72)) return;
        event.preventDefault();
      } else if (backwardKeys.includes(event.key)) {
        if (!consumeDelta(-72)) return;
        event.preventDefault();
      }
    };

    const handleTouchStart = event => {
      touchStartRef.current = event.touches[0]?.clientY ?? null;
    };

    const handleTouchMove = event => {
      const currentY = event.touches[0]?.clientY;
      if (touchStartRef.current == null || currentY == null) return;

      const deltaY = touchStartRef.current - currentY;
      if (!consumeDelta(deltaY)) {
        touchStartRef.current = currentY;
        return;
      }

      event.preventDefault();
      touchStartRef.current = currentY;
    };

    updateEligibility();
    window.addEventListener('scroll', updateEligibility, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      syncLockState(false);
      window.removeEventListener('scroll', updateEligibility);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [scrollProgress]);

  const topIconP = smoothStep(0.02, 0.24, scrollProgress);
  const botIconP = smoothStep(0.28, 0.5, scrollProgress);
  const topCardP = smoothStep(0.02, 0.48, cardSequenceProgress);
  const botCardP = smoothStep(0.54, 1.0, cardSequenceProgress);
  const replayProgress = smoothStep(1.0, REPLAY_END, cardSequenceProgress);
  const replayCardStrength = (index) => {
    const start = index * 0.18;
    const end = start + 0.24;
    return smoothStep(start, end, replayProgress);
  };

  const cards = [
    {
      side: 'left',
      eyebrow: 'Finance',
      title: 'Own Your Revenue',
      sub: 'No platform cuts. Every euro goes directly to you.',
      icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v1m0 8v1M9.5 9.5a2.5 2.5 0 0 1 5 0c0 1.5-1 2-2.5 2.5S9.5 13 9.5 14.5a2.5 2.5 0 0 0 5 0" /></svg>)
    },
    {
      side: 'left',
      eyebrow: 'Launch',
      title: 'Quick Launch',
      sub: 'After validation you can start selling immediately.',
      icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.5-2 4-2 4s2.5-.5 4-2l9-9a2.121 2.121 0 0 0-3-3l-8 10Z" /><path d="M15 5s1 1 2 3M13.5 6.5l4-4 1 1-4 4" /></svg>)
    },
    {
      side: 'right',
      eyebrow: 'Scale',
      title: 'Built for Scale',
      sub: 'From 1 to 1M attendees. Same platform, zero compromise.',
      icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></svg>)
    },
    {
      side: 'right',
      eyebrow: 'Ops',
      title: 'Always in Control',
      sub: 'Real-time data, instant payouts and accreditation applications.',
      icon: (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" /></svg>)
    },
  ];

  const leftCards = cards.filter(c => c.side === 'left');
  const rightCards = cards.filter(c => c.side === 'right');
  const energy = Math.max(topIconP, botIconP, topCardP, botCardP);
  const centerLift = (0.46 - scrollProgress) * 8;
  const featureWords = [
    { label: 'Tap2Pay', x: '7%', y: '12%', size: '10px', delay: '2.9s' },
    { label: 'POS', x: '18%', y: '17%', size: '10px', delay: '3.3s' },
    { label: 'Access control', x: '5%', y: '43%', size: '10px', delay: '0s' },
    { label: 'Validation', x: '10%', y: '27%', size: '10px', delay: '1.7s' },
    { label: 'Marketing', x: '24%', y: '34%', size: '10px', delay: '2.6s' },
    { label: 'Revenue control', x: '8%', y: '87%', size: '10px', delay: '1.1s' },
    { label: 'Capacity', x: '16%', y: '92%', size: '11px', delay: '0.5s' },
    { label: 'Split payments', x: '29%', y: '91%', size: '12px', delay: '2.2s' },
    { label: 'Dashboard', x: '83%', y: '13%', size: '10px', delay: '1.3s' },
    { label: 'PSP', x: '92%', y: '17%', size: '10px', delay: '2.5s' },
    { label: 'Statistics', x: '79%', y: '33%', size: '10px', delay: '3.1s' },
    { label: 'Accreditation', x: '94%', y: '52%', size: '10px', delay: '1.9s' },
    { label: 'Transactions', x: '8%', y: '68%', size: '10px', delay: '0.7s' },
    { label: 'Venue sync', x: '95%', y: '69%', size: '10px', delay: '2.8s' },
    { label: 'Live sync', x: '90%', y: '87%', size: '10px', delay: '2.1s' },
    { label: 'Organizer data', x: '80%', y: '93%', size: '11px', delay: '1.5s' },
    { label: 'Guestlist', x: '70%', y: '90%', size: '10px', delay: '0.9s' },
  ];

  const renderCard = (card, i, side, iconProgress, cardProgress, hoverReplayProgress = 0) => {
    const isLeft = side === 'left';
    const iconTravel = (1 - iconProgress) * 68;
    const cardTravel = (1 - cardProgress) * 86;
    const cardLockOffset = cardProgress * 18;
    const iconScale = 0.86 + iconProgress * 0.14;
    const lineScale = 0.18 + Math.max(iconProgress, cardProgress) * 0.82;
    const replayLift = hoverReplayProgress * -10;
    const replayScale = 1 + hoverReplayProgress * 0.03;
    const replayOpacityBoost = hoverReplayProgress * 0.06;
    return (
      <div key={i} className={`why-slot why-slot--${side}`}>
        <div
          className="why-slot-rail"
          style={{
            opacity: 0.18 + cardProgress * 0.82,
            transform: `scaleX(${lineScale})`,
          }}
        />
        <div className="why-floating-icon" style={{
          right: isLeft ? '8px' : 'auto',
          left: isLeft ? 'auto' : '8px',
          transform: `translate3d(${isLeft ? -iconTravel : iconTravel}vw, -50%, 0) scale(${iconScale})`,
          opacity: iconProgress,
        }}>
          <div className="why-icon-core" />
          {card.icon}
        </div>
        <div className={`why-card-body ${isLeft ? 'why-card-body--left' : 'why-card-body--right'} ${hoverReplayProgress > 0 ? 'is-hover-replay' : ''}`} style={{
          transform: `translate3d(calc(${isLeft ? -cardTravel : cardTravel}vw ${isLeft ? '+' : '-'} ${cardLockOffset}px), ${replayLift}px, 0) scale(${replayScale})`,
          opacity: Math.min(1, cardProgress + replayOpacityBoost),
        }}>
          <div className="why-card-sheen" />
          {isLeft
            ? <div className="why-card-text why-card-text--left"><div className="why-card-meta">{card.eyebrow}</div><h3 className="why-card-title">{card.title}</h3><p className="why-card-sub">{card.sub}</p></div>
            : <div className="why-card-text why-card-text--right"><div className="why-card-meta">{card.eyebrow}</div><h3 className="why-card-title">{card.title}</h3><p className="why-card-sub">{card.sub}</p></div>
          }
        </div>
      </div>
    );
  };

  return (
    <div className="why-outer" ref={outerRef}>
      <div className={`why-sticky ${isSequenceLocked ? 'is-sequence-locked' : ''}`} ref={stickyRef}>
        <div className="why-bg-orb why-bg-orb--left" />
        <div className="why-bg-orb why-bg-orb--right" />
        <div className="why-bg-grid" />
        <div className="why-word-cloud" aria-hidden="true">
          {featureWords.map(word => (
            <span
              key={word.label}
              className="why-word-cloud-item"
              style={{
                left: word.x,
                top: word.y,
                fontSize: word.size,
                animationDelay: word.delay,
                    opacity: 0.14 + energy * 0.5,
                  }}
                >
                  {word.label}
                </span>
              ))}
        </div>

        <div className="why-title-overlay why-title-overlay--headline-only">
          <p className="why-headline-small">Why organizers switch</p>
        </div>

        <div className="why-inner">
          <div className="why-col why-col--left">
            {renderCard(leftCards[0], 0, 'left', topIconP, topCardP, replayCardStrength(0))}
            {renderCard(leftCards[1], 1, 'left', botIconP, botCardP, replayCardStrength(1))}
          </div>

          <div className="why-center">
            <div className="why-center-spine" />
            <div className="why-center-shell" style={{ transform: `translateY(${centerLift}px)` }}>
              <div className="why-center-core">
                <div className="why-center-aura" style={{ opacity: 0.35 + energy * 0.4 }} />
                <div className="why-center-orbit why-center-orbit--outer" />
                <div className="why-center-orbit why-center-orbit--inner" />
                <div className="why-photo-wrap">
                  <img
                    src="/images/why%20section.JPEG"
                    alt=""
                    className="why-photo"
                    style={{ transform: `scale(1.03) translateY(${centerLift * -0.38}px)` }}
                  />
                  <div className="why-photo-grade" />
                  <div className="why-photo-shine" />
                  <div className="why-photo-vignette" />
                </div>
                <div className="scroll-indicator why-scroll-indicator" aria-hidden="true">
                  <span className="scroll-text">Scroll to explore</span>
                  <div className="scroll-line-gradient"></div>
                </div>
                <div className="why-center-pulse why-center-pulse--one" />
                <div className="why-center-pulse why-center-pulse--two" />
                <div className="why-center-core-dot" />
              </div>
            </div>
          </div>

          <div className="why-col why-col--right">
            {renderCard(rightCards[0], 0, 'right', topIconP, topCardP, replayCardStrength(2))}
            {renderCard(rightCards[1], 1, 'right', botIconP, botCardP, replayCardStrength(3))}
          </div>
        </div>

      </div>
    </div>
  );
}

function IntegrationsHub() {
  // Eén aaneengesloten SVG pad per node:
  //   M center  →  L entry_punt  →  CW border rond node  →  L entry_punt
  // strokeDasharray groeit van 0 naar total_length. Geen dashoffset.
  const PATH_DATA = {
    mobile: { d: `M 480.00,190.00 L 714.66,103.83 A 14,14 0 0 1 710.56,93.93 L 710.56,73.93 A 14,14 0 0 1 724.56,59.93 L 744.56,59.93 A 14,14 0 0 1 758.56,73.93 L 758.56,93.93 A 14,14 0 0 1 744.56,107.93 L 724.56,107.93 L 714.66,103.83`, total: 417.94 },
    api: { d: `M 480.00,190.00 L 816.00,190.00 L 816.00,180.00 A 14,14 0 0 1 830.00,166.00 L 850.00,166.00 A 14,14 0 0 1 864.00,180.00 L 864.00,200.00 A 14,14 0 0 1 850.00,214.00 L 830.00,214.00 A 14,14 0 0 1 816.00,200.00 L 816.00,190.00`, total: 503.96 },
    psp: { d: `M 480.00,190.00 L 714.66,276.17 A 14,14 0 0 1 724.56,272.07 L 744.56,272.07 A 14,14 0 0 1 758.56,286.07 L 758.56,306.07 A 14,14 0 0 1 744.56,320.07 L 724.56,320.07 A 14,14 0 0 1 710.56,306.07 L 710.56,286.07 L 714.66,276.17`, total: 417.94 },
    pos: { d: `M 480.00,190.00 L 480.00,316.00 L 490.00,316.00 A 14,14 0 0 1 504.00,330.00 L 504.00,350.00 A 14,14 0 0 1 490.00,364.00 L 470.00,364.00 A 14,14 0 0 1 456.00,350.00 L 456.00,330.00 A 14,14 0 0 1 470.00,316.00 L 480.00,316.00`, total: 293.96 },
    crm: { d: `M 480.00,190.00 L 245.34,276.17 A 14,14 0 0 1 249.44,286.07 L 249.44,306.07 A 14,14 0 0 1 235.44,320.07 L 215.44,320.07 A 14,14 0 0 1 201.44,306.07 L 201.44,286.07 A 14,14 0 0 1 215.44,272.07 L 235.44,272.07 L 245.34,276.17`, total: 417.94 },
    affiliate: { d: `M 480.00,190.00 L 144.00,190.00 L 144.00,200.00 A 14,14 0 0 1 130.00,214.00 L 110.00,214.00 A 14,14 0 0 1 96.00,200.00 L 96.00,180.00 A 14,14 0 0 1 110.00,166.00 L 130.00,166.00 A 14,14 0 0 1 144.00,180.00 L 144.00,190.00`, total: 503.96 },
    zapier: { d: `M 480.00,190.00 L 245.34,103.83 A 14,14 0 0 1 235.44,107.93 L 215.44,107.93 A 14,14 0 0 1 201.44,93.93 L 201.44,73.93 A 14,14 0 0 1 215.44,59.93 L 235.44,59.93 A 14,14 0 0 1 249.44,73.93 L 249.44,93.93 L 245.34,103.83`, total: 417.94 },
  };

  const TOTAL_MS = 3000;
  const LINE_FRAC = 0.42;  // fractie van total_length die de LIJN beslaat
  // Border = rest van het pad (1 - LINE_FRAC)

  const [tick, setTick] = useState({ node: 0, p: 0, ringP: 0 });

  useEffect(() => {
    let t0 = null, raf;
    const loop = ts => {
      if (!t0) t0 = ts;
      const el = ts - t0;
      setTick({
        node: Math.floor(el / TOTAL_MS) % 7,
        p: (el % TOTAL_MS) / TOTAL_MS,
        ringP: (el % (TOTAL_MS * 1.8)) / (TOTAL_MS * 1.8),
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const W = 960, H = 380, CX = 480, CY = 190, RX = 360, RY = 150;

  const nodes = [
    { id: 'mobile', angle: 315, label: 'Mobile Scanning', sub: 'Entry & access ID', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" /><rect x="7" y="7" width="10" height="10" rx="1" /></svg> },
    { id: 'api', angle: 0, label: 'API', sub: 'Custom connections', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M8 9l-3 3 3 3M16 9l3 3-3 3M13 6l-2 12" /></svg> },
    { id: 'psp', angle: 45, label: 'PSP', sub: 'Payment processing', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="3" /><path d="M2 10h20M6 15h4" /><circle cx="17" cy="15" r="1.5" fill="currentColor" stroke="none" /></svg> },
    { id: 'pos', angle: 90, label: 'POS Systems', sub: 'Tap2Pay', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /><path d="M6 8h.01M10 8h4" /></svg> },
    { id: 'crm', angle: 135, label: 'CRM', sub: 'Customer data', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg> },
    { id: 'affiliate', angle: 180, label: 'Affiliate', sub: 'Partner tracking', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" /></svg> },
    { id: 'zapier', angle: 225, label: 'Agents', sub: 'Workflow automation', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" /></svg> },
  ];

  const positions = nodes.map(n => {
    const rad = n.angle * Math.PI / 180;
    return { ...n, px: CX + RX * Math.cos(rad), py: CY + RY * Math.sin(rad) };
  });

  const eo = t => { const c = Math.max(0, Math.min(1, t)); return 1 - (1 - c) * (1 - c) * (1 - c); };

  const rings = [0, 1, 2].map(i => {
    const rp = ((tick.ringP - i / 3) + 1) % 1;
    const a = rp < 0.38 ? rp / 0.38 : 0;
    return { r: 44 + a * 175, opacity: a > 0 ? (1 - a) * 0.55 : 0 };
  });

  const { node, p } = tick;
  // Cubic ease-out over de volledige cyclus
  const animP = eo(Math.min(p / 0.88, 1));  // klaar bij p=0.88, dan pauze

  return (
    <section className="integ-section">
      <div className="boundary-marker boundary-marker--dark" aria-hidden="true" />
      <div className="integ-container">
        <div className="integ-header">
          <p className="integ-eyebrow">INTEGRATIONS</p>
          <h2 className="integ-title">Connected to Your Own <span className="accent-text">Workflow</span></h2>
          <p className="integ-sub">Tick-etc. connects your apps — no rebuilding required.</p>
        </div>

        <div className="integ-diagram" style={{ width: '100%', maxWidth: W, aspectRatio: `${W}/${H}`, position: 'relative' }}>
          <svg viewBox={`0 0 ${W} ${H}`} className="integ-svg" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2.5" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              {positions.map(n => (
                <linearGradient key={n.id} id={`lg-${n.id}`}
                  x1={CX} y1={CY} x2={n.px} y2={n.py} gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#6EE7FF" />
                  <stop offset="100%" stopColor="#B482FF" />
                </linearGradient>
              ))}
            </defs>

            <ellipse cx={CX} cy={CY} rx={RX} ry={RY}
              fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" strokeDasharray="3 9" />

            {/* Basislijnen: lopen exact over de animatielijn (center → entry punt op border) */}
            {positions.map(n => {
              const pd = PATH_DATA[n.id];
              // Tweede punt in het pad is het entry punt: "M cx,cy L ex,ey ..."
              const parts = pd.d.split(' ');
              const ex = parts[4], ey = parts[5];
              return (
                <path key={`bl-${n.id}`}
                  d={`M ${CX},${CY} L ${ex},${ey}`}
                  stroke="rgba(255,255,255,0.07)" strokeWidth="1" fill="none" />
              );
            })}

            {rings.map((ring, i) => (
              <circle key={i} cx={CX} cy={CY} r={ring.r}
                fill="none" stroke="rgba(110,231,255,0.65)" strokeWidth="1" opacity={ring.opacity} />
            ))}

            {/* Alle 7 paden altijd aanwezig — geen unmount/remount flash */}
            {positions.map((n, i) => {
              const pd = PATH_DATA[n.id];
              const isActive = i === node;
              // drawn groeit van 0 naar pd.total tijdens de actieve cyclus
              const drawn = isActive ? animP * pd.total : 0;
              return (
                <path
                  key={n.id}
                  d={pd.d}
                  fill="none"
                  stroke={`url(#lg-${n.id})`}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  filter="url(#glow)"
                  strokeDasharray={`${drawn} 9999`}
                  strokeDashoffset="0"
                  opacity={isActive ? 1 : 0}
                />
              );
            })}
          </svg>

          <div className="integ-center">
            <div className="integ-center-inner">
              <span className="integ-center-label">Tick-etc.</span>
            </div>
          </div>

          {positions.map((n, i) => {
            const isActive = i === node;
            const showActive = isActive && animP > 0.48;
            const isLeft = n.px < CX - 80;
            const isRight = n.px > CX + 80;
            const isBelow = n.py > CY + 50;
            const lbl = isLeft ? 'integ-node-label--left'
              : isRight ? 'integ-node-label--right'
                : isBelow ? 'integ-node-label--below'
                  : 'integ-node-label--above';
            return (
              <div key={n.id}
                className={`integ-node ${showActive ? 'integ-node--active' : ''}`}
                style={{ left: `${(n.px / W) * 100}%`, top: `${(n.py / H) * 100}%` }}
              >
                <div className="integ-node-icon">{n.icon}</div>
                <div className={`integ-node-label ${lbl}`}>
                  <span className="integ-node-name">{n.label}</span>
                  <span className="integ-node-sub">{n.sub}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="integ-ripple-layer" aria-hidden="true">
        <RippleGrid
          gridColor="#8FDBFF"
          rippleIntensity={0.026}
          gridSize={18}
          gridThickness={30}
          fadeDistance={3.4}
          vignetteStrength={2.45}
          glowIntensity={0.075}
          opacity={0.36}
          gridRotation={0}
          mouseInteraction={true}
          mouseInteractionRadius={1.9}
          mouseRippleStrength={1.45}
          mouseGlowStrength={0.16}
        />
      </div>
    </section>
  );
}

const PLATFORM_FOLDER_THEMES = {
  ticketing: {
    accent: '#6EE7FF',
    ink: '#0C2538',
    bg: 'linear-gradient(135deg, #DFF6FF 0%, #A8DDFF 46%, #7FA8FF 100%)',
    tab: 'linear-gradient(135deg, #86E8FF, #76A9FF)',
  },
  payments: {
    accent: '#22C55E',
    ink: '#0F2F26',
    bg: 'linear-gradient(135deg, #DCFCE7 0%, #8AE6C1 48%, #38BDF8 100%)',
    tab: 'linear-gradient(135deg, #22C55E, #6EE7FF)',
  },
  seatmaps: {
    accent: '#B482FF',
    ink: '#271B3F',
    bg: 'linear-gradient(135deg, #F0E8FF 0%, #B9A7FF 45%, #6EE7FF 100%)',
    tab: 'linear-gradient(135deg, #B482FF, #6EE7FF)',
  },
  dashboard: {
    accent: '#0066FF',
    ink: '#102B4A',
    bg: 'linear-gradient(135deg, #E8F0F7 0%, #8FDBFF 44%, #0066FF 100%)',
    tab: 'linear-gradient(135deg, #8FDBFF, #0066FF)',
  },
};

function PlatformFolderStack({ productTabs, deckOrder, activeTab, setActiveTab }) {
  const tabKeys = Object.keys(productTabs);
  const themeByPosition = {
    1: PLATFORM_FOLDER_THEMES.seatmaps,
    2: PLATFORM_FOLDER_THEMES.dashboard,
    3: PLATFORM_FOLDER_THEMES.payments,
  };
  const displayTitles = {
    ...Object.fromEntries(tabKeys.map((key) => [key, productTabs[key].title])),
    seatmaps: 'SEATS & ACCREDITATION',
  };
  const labelXByStackPosition = {
    0: '12.5%',
    3: '37.5%',
    2: '62.5%',
    1: '87.5%',
  };
  const positionByDeckIndex = [0, 3, 2, 1];
  const slots = deckOrder
    .map((id, index) => ({
      id,
      position: positionByDeckIndex[index],
      isActive: index === 0,
    }))
    .filter((slot) => slot.id);

  return (
    <div className="ps-depth-stack" style={{ '--active-accent': PLATFORM_FOLDER_THEMES[activeTab].accent }}>
      {slots.map(({ id, position, isActive }) => {
        const theme = isActive ? PLATFORM_FOLDER_THEMES[id] : themeByPosition[position];

        return (
          <div
            key={id}
            role="button"
            tabIndex={0}
            className={`ps-depth-card ${isActive ? 'ps-depth-card--active' : ''}`}
            onClick={() => !isActive && setActiveTab(id)}
            onKeyDown={(event) => {
              if (!isActive && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                setActiveTab(id);
              }
            }}
            aria-current={isActive ? 'true' : undefined}
            style={{
              '--folder-bg': theme.bg,
              '--folder-accent': theme.accent,
              '--folder-ink': theme.ink,
              '--stack-top': `${position === 0 ? 96 : (position - 1) * 32}px`,
              '--stack-x': `${position === 0 ? 0 : (4 - position) * 18}px`,
              '--label-x': labelXByStackPosition[position],
              '--stack-z': position,
            }}
          >
            <span className="ps-depth-card__tab">{displayTitles[id]}</span>

            {isActive && (
              <span className="ps-depth-card__content">
                <span className="ps-depth-card__body">
                  <h3 className="ps-panel-title">{productTabs[activeTab].desc}</h3>
                  <div className="ps-content-row">
                    <ul className="ps-features">
                      {productTabs[activeTab].features.map((feature, i) => (
                        <li key={i} className="ps-feature-item">
                          <span className="ps-feature-check" style={{ color: theme.accent }}>✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="ps-bento">
                      <div className="ps-bento-inner">
                        <PsAnimation tab={activeTab} />
                        {activeTab === 'ticketing' && (
                          <div className="ps-mockup-wrap">
                            <img src="/ticket_app.png" alt="Ticket app" className="ps-mockup-img" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {activeTab === 'seatmaps' && (
                    <div className="ps-deepdive-note">
                      A DEEPDIVE FEATURE ANIMATED EXPLENATION IN NEXT SECTION
                    </div>
                  )}
                </span>
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function parseHSL(hslStr) {
  const match = hslStr.match(/([\d.]+)\s*([\d.]+)%?\s*([\d.]+)%?/);
  if (!match) return { h: 40, s: 80, l: 80 };
  return { h: parseFloat(match[1]), s: parseFloat(match[2]), l: parseFloat(match[3]) };
}

function buildGlowVars(glowColor, intensity) {
  const { h, s, l } = parseHSL(glowColor);
  const base = `${h}deg ${s}% ${l}%`;
  const opacities = [100, 60, 50, 40, 30, 20, 10];
  const keys = ['', '-60', '-50', '-40', '-30', '-20', '-10'];
  const vars = {};

  for (let i = 0; i < opacities.length; i += 1) {
    vars[`--glow-color${keys[i]}`] = `hsl(${base} / ${Math.min(opacities[i] * intensity, 100)}%)`;
  }

  return vars;
}

const BORDER_GRADIENT_POSITIONS = ['80% 55%', '69% 34%', '8% 6%', '41% 38%', '86% 85%', '82% 18%', '51% 4%'];
const BORDER_GRADIENT_KEYS = ['--gradient-one', '--gradient-two', '--gradient-three', '--gradient-four', '--gradient-five', '--gradient-six', '--gradient-seven'];
const BORDER_COLOR_MAP = [0, 1, 2, 0, 1, 2, 1];

function buildGradientVars(colors) {
  const vars = {};

  for (let i = 0; i < 7; i += 1) {
    const color = colors[Math.min(BORDER_COLOR_MAP[i], colors.length - 1)];
    vars[BORDER_GRADIENT_KEYS[i]] = `radial-gradient(at ${BORDER_GRADIENT_POSITIONS[i]}, ${color} 0px, transparent 50%)`;
  }

  vars['--gradient-base'] = `linear-gradient(${colors[0]} 0 100%)`;
  return vars;
}

function easeOutCubic(x) {
  return 1 - Math.pow(1 - x, 3);
}

function easeInCubic(x) {
  return x * x * x;
}

function animateValue({ start = 0, end = 100, duration = 1000, delay = 0, ease = easeOutCubic, onUpdate, onEnd, shouldContinue }) {
  const t0 = performance.now() + delay;

  function tick() {
    if (shouldContinue && !shouldContinue()) return;
    const elapsed = performance.now() - t0;
    const t = Math.min(elapsed / duration, 1);
    onUpdate(start + (end - start) * ease(t));

    if (t < 1) {
      requestAnimationFrame(tick);
    } else if (onEnd) {
      onEnd();
    }
  }

  setTimeout(() => requestAnimationFrame(tick), delay);
}

function BorderGlow({
  children,
  className = '',
  edgeSensitivity = 30,
  glowColor = '40 80 80',
  backgroundColor = '#060010',
  borderRadius = 28,
  glowRadius = 40,
  glowIntensity = 1,
  coneSpread = 25,
  animated = false,
  followMouse = true,
  hoverSweep = false,
  colors = ['#c084fc', '#f472b6', '#38bdf8'],
  fillOpacity = 0.5,
}) {
  const cardRef = useRef(null);
  const hoverActiveRef = useRef(false);
  const sweepTokenRef = useRef(0);

  const getCenterOfElement = useCallback((el) => {
    const { width, height } = el.getBoundingClientRect();
    return [width / 2, height / 2];
  }, []);

  const getEdgeProximity = useCallback((el, x, y) => {
    const [cx, cy] = getCenterOfElement(el);
    const dx = x - cx;
    const dy = y - cy;
    let kx = Infinity;
    let ky = Infinity;
    if (dx !== 0) kx = cx / Math.abs(dx);
    if (dy !== 0) ky = cy / Math.abs(dy);
    return Math.min(Math.max(1 / Math.min(kx, ky), 0), 1);
  }, [getCenterOfElement]);

  const getCursorAngle = useCallback((el, x, y) => {
    const [cx, cy] = getCenterOfElement(el);
    const dx = x - cx;
    const dy = y - cy;
    if (dx === 0 && dy === 0) return 0;
    const radians = Math.atan2(dy, dx);
    let degrees = radians * (180 / Math.PI) + 90;
    if (degrees < 0) degrees += 360;
    return degrees;
  }, [getCenterOfElement]);

  const handlePointerMove = useCallback((e) => {
    if (!followMouse) return;
    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const edge = getEdgeProximity(card, x, y);
    const angle = getCursorAngle(card, x, y);

    card.style.setProperty('--edge-proximity', `${(edge * 100).toFixed(3)}`);
    card.style.setProperty('--cursor-angle', `${angle.toFixed(3)}deg`);
  }, [followMouse, getCursorAngle, getEdgeProximity]);

  const runSweep = useCallback((shouldLoop = false) => {
    const card = cardRef.current;
    if (!card) return;

    const token = ++sweepTokenRef.current;
    const isCurrent = () => token === sweepTokenRef.current;
    const angleStart = 110;
    const angleEnd = 465;

    card.classList.add('sweep-active');
    card.style.setProperty('--cursor-angle', `${angleStart}deg`);
    card.style.setProperty('--edge-proximity', '100');

    animateValue({
      ease: easeInCubic,
      duration: 850,
      start: angleStart,
      end: angleStart + (angleEnd - angleStart) * 0.42,
      onUpdate: (v) => card.style.setProperty('--cursor-angle', `${v}deg`),
      shouldContinue: isCurrent,
    });

    animateValue({
      ease: easeOutCubic,
      delay: 850,
      duration: 1350,
      start: angleStart + (angleEnd - angleStart) * 0.42,
      end: angleEnd,
      onUpdate: (v) => card.style.setProperty('--cursor-angle', `${v}deg`),
      shouldContinue: isCurrent,
    });

    animateValue({
      ease: easeInCubic,
      delay: 1650,
      duration: 700,
      shouldContinue: isCurrent,
      onEnd: () => {
        if (!isCurrent()) return;
        if (shouldLoop && hoverActiveRef.current) {
          runSweep(true);
          return;
        }
        card.style.setProperty('--edge-proximity', '0');
        card.classList.remove('sweep-active');
      },
    });
  }, []);

  useEffect(() => {
    if (!animated || !cardRef.current) return;

    runSweep(false);
  }, [animated, runSweep]);

  const handlePointerEnter = useCallback(() => {
    if (!hoverSweep || followMouse) return;
    hoverActiveRef.current = true;
    runSweep(true);
  }, [followMouse, hoverSweep, runSweep]);

  const handlePointerLeave = useCallback(() => {
    const card = cardRef.current;
    hoverActiveRef.current = false;
    sweepTokenRef.current += 1;
    if (!card) return;
    card.classList.remove('sweep-active');
    card.style.setProperty('--edge-proximity', '0');
  }, []);

  return (
    <div
      ref={cardRef}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      onPointerMove={handlePointerMove}
      className={`border-glow-card ${className}`}
      style={{
        '--card-bg': backgroundColor,
        '--edge-sensitivity': edgeSensitivity,
        '--border-radius': `${borderRadius}px`,
        '--glow-padding': `${glowRadius}px`,
        '--cone-spread': coneSpread,
        '--fill-opacity': fillOpacity,
        ...buildGlowVars(glowColor, glowIntensity),
        ...buildGradientVars(colors),
      }}
    >
      <span className="edge-light" />
      <div className="border-glow-inner">
        {children}
      </div>
    </div>
  );
}



function RippleGrid({
  enableRainbow = false,
  gridColor = '#ffffff',
  rippleIntensity = 0.05,
  gridSize = 10,
  gridThickness = 15,
  fadeDistance = 1.5,
  vignetteStrength = 2,
  glowIntensity = 0.1,
  opacity = 1,
  gridRotation = 0,
  mouseInteraction = true,
  mouseInteractionRadius = 1,
  mouseRippleStrength = 0.72,
  mouseGlowStrength = 0,
}) {
  const containerRef = useRef(null);
  const uniformsRef = useRef(null);
  const frameRef = useRef(0);
  const mousePositionRef = useRef({ x: 0.5, y: 0.5 });
  const targetMouseRef = useRef({ x: 0.5, y: 0.5 });
  const mouseInfluenceRef = useRef(0);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? [parseInt(result[1], 16) / 255, parseInt(result[2], 16) / 255, parseInt(result[3], 16) / 255]
        : [1, 1, 1];
    };

    const renderer = new Renderer({
      dpr: Math.min(window.devicePixelRatio, 2),
      alpha: true,
    });

    const gl = renderer.gl;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.canvas.style.width = '100%';
    gl.canvas.style.height = '100%';
    gl.canvas.style.display = 'block';
    containerRef.current.appendChild(gl.canvas);

    const vertex = `
attribute vec2 position;
varying vec2 vUv;
void main() {
  vUv = position * 0.5 + 0.5;
  gl_Position = vec4(position, 0.0, 1.0);
}`;

    const fragment = `precision highp float;
uniform float iTime;
uniform vec2 iResolution;
uniform bool enableRainbow;
uniform vec3 gridColor;
uniform float rippleIntensity;
uniform float gridSize;
uniform float gridThickness;
uniform float fadeDistance;
uniform float vignetteStrength;
uniform float glowIntensity;
uniform float opacity;
uniform float gridRotation;
uniform bool mouseInteraction;
uniform vec2 mousePosition;
uniform float mouseInfluence;
uniform float mouseInteractionRadius;
uniform float mouseRippleStrength;
uniform float mouseGlowStrength;
varying vec2 vUv;

float pi = 3.14159265359;

mat2 rotate(float angle) {
  float s = sin(angle);
  float c = cos(angle);
  return mat2(c, -s, s, c);
}

void main() {
  vec2 uv = vUv * 2.0 - 1.0;
  uv.x *= iResolution.x / iResolution.y;

  if (gridRotation != 0.0) {
    uv = rotate(gridRotation * pi / 180.0) * uv;
  }

  float dist = length(uv);
  float slowTime = iTime * 0.62;
  float func = sin(pi * (slowTime - dist));
  vec2 rippleUv = uv + uv * func * rippleIntensity;
  float mouseGlow = 0.0;

  if (mouseInteraction && mouseInfluence > 0.0) {
    vec2 mouseUv = mousePosition * 2.0 - 1.0;
    mouseUv.x *= iResolution.x / iResolution.y;
    float mouseDist = length(uv - mouseUv);
    float influence = mouseInfluence * exp(-mouseDist * mouseDist / (mouseInteractionRadius * mouseInteractionRadius));
    mouseGlow = influence;
    vec2 dir = mouseDist > 0.0001 ? normalize(uv - mouseUv) : vec2(0.0);
    float mouseWave = sin(pi * (slowTime * 2.15 - mouseDist * 3.0)) * influence;
    rippleUv += dir * mouseWave * rippleIntensity * mouseRippleStrength;
  }

  vec2 a = sin(gridSize * 0.5 * pi * rippleUv - pi / 2.0);
  vec2 b = abs(a);
  float aaWidth = 0.5;
  vec2 smoothB = vec2(
    smoothstep(0.0, aaWidth, b.x),
    smoothstep(0.0, aaWidth, b.y)
  );

  vec3 color = vec3(0.0);
  color += exp(-gridThickness * smoothB.x * (0.8 + 0.5 * sin(pi * slowTime)));
  color += exp(-gridThickness * smoothB.y);
  color += 0.5 * exp(-(gridThickness / 4.0) * sin(smoothB.x));
  color += 0.5 * exp(-(gridThickness / 3.0) * smoothB.y);

  if (glowIntensity > 0.0) {
    color += glowIntensity * exp(-gridThickness * 0.5 * smoothB.x);
    color += glowIntensity * exp(-gridThickness * 0.5 * smoothB.y);
  }

  if (mouseGlowStrength > 0.0) {
    color += mouseGlowStrength * mouseGlow * exp(-gridThickness * 0.35 * smoothB.x);
    color += mouseGlowStrength * mouseGlow * exp(-gridThickness * 0.35 * smoothB.y);
  }

  float ddd = exp(-2.0 * clamp(pow(dist, fadeDistance), 0.0, 1.0));
  vec2 vignetteCoords = vUv - 0.5;
  float vignetteDistance = length(vignetteCoords);
  float vignette = 1.0 - pow(vignetteDistance * 2.0, vignetteStrength);
  vignette = clamp(vignette, 0.0, 1.0);

  vec3 tint;
  if (enableRainbow) {
    tint = vec3(
      uv.x * 0.5 + 0.5 * sin(iTime),
      uv.y * 0.5 + 0.5 * cos(iTime),
      pow(cos(iTime), 4.0)
    ) + 0.5;
  } else {
    tint = gridColor;
  }

  float finalFade = ddd * vignette;
  float alpha = length(color) * finalFade * opacity;
  gl_FragColor = vec4(color * tint * finalFade * opacity, alpha);
}`;

    const uniforms = {
      iTime: { value: 0 },
      iResolution: { value: [1, 1] },
      enableRainbow: { value: enableRainbow },
      gridColor: { value: hexToRgb(gridColor) },
      rippleIntensity: { value: rippleIntensity },
      gridSize: { value: gridSize },
      gridThickness: { value: gridThickness },
      fadeDistance: { value: fadeDistance },
      vignetteStrength: { value: vignetteStrength },
      glowIntensity: { value: glowIntensity },
      opacity: { value: opacity },
      gridRotation: { value: gridRotation },
      mouseInteraction: { value: mouseInteraction },
      mousePosition: { value: [0.5, 0.5] },
      mouseInfluence: { value: 0 },
      mouseInteractionRadius: { value: mouseInteractionRadius },
      mouseRippleStrength: { value: mouseRippleStrength },
      mouseGlowStrength: { value: mouseGlowStrength },
    };

    uniformsRef.current = uniforms;

    const geometry = new Triangle(gl);
    const program = new Program(gl, { vertex, fragment, uniforms });
    const mesh = new Mesh(gl, { geometry, program });

    const resize = () => {
      if (!containerRef.current) return;
      const { clientWidth: w, clientHeight: h } = containerRef.current;
      renderer.setSize(w, h);
      uniforms.iResolution.value = [w, h];
    };

    const handleMouseMove = (e) => {
      if (!mouseInteraction || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      targetMouseRef.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: 1 - (e.clientY - rect.top) / rect.height,
      };
    };

    const handleMouseEnter = () => {
      if (!mouseInteraction) return;
      mouseInfluenceRef.current = 1;
    };

    const handleMouseLeave = () => {
      if (!mouseInteraction) return;
      mouseInfluenceRef.current = 0;
    };

    const container = containerRef.current;
    window.addEventListener('resize', resize);
    if (mouseInteraction) {
      container.addEventListener('mousemove', handleMouseMove);
      container.addEventListener('mouseenter', handleMouseEnter);
      container.addEventListener('mouseleave', handleMouseLeave);
    }
    resize();

    const render = (time) => {
      uniforms.iTime.value = time * 0.001;
      mousePositionRef.current.x += (targetMouseRef.current.x - mousePositionRef.current.x) * 0.1;
      mousePositionRef.current.y += (targetMouseRef.current.y - mousePositionRef.current.y) * 0.1;
      uniforms.mouseInfluence.value += (mouseInfluenceRef.current - uniforms.mouseInfluence.value) * 0.05;
      uniforms.mousePosition.value = [mousePositionRef.current.x, mousePositionRef.current.y];
      renderer.render({ scene: mesh });
      frameRef.current = requestAnimationFrame(render);
    };

    frameRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(frameRef.current);
      window.removeEventListener('resize', resize);
      if (mouseInteraction) {
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseenter', handleMouseEnter);
        container.removeEventListener('mouseleave', handleMouseLeave);
      }
      renderer.gl.getExtension('WEBGL_lose_context')?.loseContext();
      if (container.contains(gl.canvas)) {
        container.removeChild(gl.canvas);
      }
    };
  }, []);

  useEffect(() => {
    if (!uniformsRef.current) return;

    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? [parseInt(result[1], 16) / 255, parseInt(result[2], 16) / 255, parseInt(result[3], 16) / 255]
        : [1, 1, 1];
    };

    uniformsRef.current.enableRainbow.value = enableRainbow;
    uniformsRef.current.gridColor.value = hexToRgb(gridColor);
    uniformsRef.current.rippleIntensity.value = rippleIntensity;
    uniformsRef.current.gridSize.value = gridSize;
    uniformsRef.current.gridThickness.value = gridThickness;
    uniformsRef.current.fadeDistance.value = fadeDistance;
    uniformsRef.current.vignetteStrength.value = vignetteStrength;
    uniformsRef.current.glowIntensity.value = glowIntensity;
    uniformsRef.current.opacity.value = opacity;
    uniformsRef.current.gridRotation.value = gridRotation;
    uniformsRef.current.mouseInteraction.value = mouseInteraction;
    uniformsRef.current.mouseInteractionRadius.value = mouseInteractionRadius;
    uniformsRef.current.mouseRippleStrength.value = mouseRippleStrength;
    uniformsRef.current.mouseGlowStrength.value = mouseGlowStrength;
  }, [
    enableRainbow,
    gridColor,
    rippleIntensity,
    gridSize,
    gridThickness,
    fadeDistance,
    vignetteStrength,
    glowIntensity,
    opacity,
    gridRotation,
    mouseInteraction,
    mouseInteractionRadius,
    mouseRippleStrength,
    mouseGlowStrength,
  ]);

  return <div ref={containerRef} className="ripple-grid-container" />;
}

function RippleTransition({ rippleTargetRef, showScene = true, showGuide = true, onSequenceStepChange }) {
  const sentinelRef = useRef(null);
  const sectionRef = useRef(null);
  const finalDotAnchorRef = useRef(null);
  const lockRef = useRef(false);
  const eligibleRef = useRef(false);
  const sequenceStepRef = useRef(0);
  const wheelAccumulatorRef = useRef(0);
  const touchStartRef = useRef(null);
  const [origin, setOrigin] = useState({ x: 0, y: 88 });
  const [sceneSize, setSceneSize] = useState({ w: 0, h: 0 });
  const [sequenceStep, setSequenceStep] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const TOTAL_DOTS = 7;
  const FINAL_STEP = TOTAL_DOTS + 2;

  const waves = useMemo(() => ([
    { delay: 0.00, dur: 4.4, spread: 0.34, roundness: 0.42, centerY: 20, band: 28, cut: 17, lift: 7, crestLift: 10, shadowDrop: 12, opacity: 0.96 },
    { delay: 1.08, dur: 5.6, spread: 0.52, roundness: 0.56, centerY: 24, band: 34, cut: 21, lift: 10, crestLift: 14, shadowDrop: 16, opacity: 0.76 },
    { delay: 2.42, dur: 6.9, spread: 0.70, roundness: 0.69, centerY: 29, band: 40, cut: 25, lift: 13, crestLift: 18, shadowDrop: 20, opacity: 0.56 },
    { delay: 3.96, dur: 8.4, spread: 0.86, roundness: 0.80, centerY: 35, band: 46, cut: 29, lift: 16, crestLift: 22, shadowDrop: 24, opacity: 0.36 },
  ]), []);

  useEffect(() => {
    sequenceStepRef.current = sequenceStep;
  }, [sequenceStep, FINAL_STEP]);

  useEffect(() => {
    onSequenceStepChange?.(sequenceStep);
  }, [onSequenceStepChange, sequenceStep]);

  useEffect(() => {
    const sentinelEl = sentinelRef.current;
    if (!sentinelEl) return;

    const parentSection = sentinelEl.closest('.final-cta');
    if (!parentSection) return;
    sectionRef.current = parentSection;

    const measureGeometry = () => {
      const sectionEl = sectionRef.current;
      const anchorEl = rippleTargetRef?.current || finalDotAnchorRef.current;
      if (!sectionEl || !anchorEl) return;

      const sRect = sectionEl.getBoundingClientRect();
      const aRect = anchorEl.getBoundingClientRect();

      const nextOrigin = {
        x: aRect.left + aRect.width / 2 - sRect.left,
        y: aRect.top + aRect.height / 2 - sRect.top + 38,
      };

      setOrigin(prev => {
        if (Math.abs(prev.x - nextOrigin.x) < 0.5 && Math.abs(prev.y - nextOrigin.y) < 0.5) {
          return prev;
        }
        return nextOrigin;
      });

      setSceneSize(prev => {
        const w = Math.round(sRect.width);
        const h = Math.round(sRect.height);
        if (prev.w === w && prev.h === h) return prev;
        return { w, h };
      });
    };

    measureGeometry();
    window.addEventListener('resize', measureGeometry);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measureGeometry());
      ro.observe(parentSection);
      if (finalDotAnchorRef.current) ro.observe(finalDotAnchorRef.current);
      if (rippleTargetRef?.current) ro.observe(rippleTargetRef.current);
    }

    let io;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(() => measureGeometry(), { threshold: [0, 0.25, 0.5, 0.75, 1] });
      io.observe(parentSection);
    }

    return () => {
      window.removeEventListener('resize', measureGeometry);
      if (ro) ro.disconnect();
      if (io) io.disconnect();
    };
  }, [rippleTargetRef]);

  useEffect(() => {
    const sectionEl = sectionRef.current;
    if (!sectionEl) return undefined;

    const syncLockState = (nextLocked) => {
      lockRef.current = nextLocked;
      setIsLocked(prev => (prev === nextLocked ? prev : nextLocked));
    };

    const updateEligibility = () => {
      const rect = sectionEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      eligibleRef.current = rect.top <= viewportHeight * 0.74 && rect.bottom >= viewportHeight * 0.36;

      if (!eligibleRef.current && lockRef.current) {
        syncLockState(false);
      }
    };

    const stepSequence = (direction) => {
      const current = sequenceStepRef.current;
      const next = direction > 0
        ? Math.min(current + 1, FINAL_STEP)
        : Math.max(current - 1, 0);

      if (next === current) {
        syncLockState(false);
        return false;
      }

      sequenceStepRef.current = next;
      setSequenceStep(next);

      const shouldStayLocked = direction > 0
        ? next < FINAL_STEP
        : next > 0;

      syncLockState(eligibleRef.current && shouldStayLocked);
      wheelAccumulatorRef.current = 0;

      return true;
    };

    const consumeDelta = (deltaY) => {
      updateEligibility();
      if (!eligibleRef.current || deltaY === 0) return false;

      const direction = deltaY > 0 ? 1 : -1;
      const current = sequenceStepRef.current;
      const canConsume = direction > 0
        ? current < FINAL_STEP
        : current > 0;

      if (!canConsume) {
        syncLockState(false);
        wheelAccumulatorRef.current = 0;
        return false;
      }

      syncLockState(true);

      if (wheelAccumulatorRef.current !== 0 && Math.sign(wheelAccumulatorRef.current) !== Math.sign(deltaY)) {
        wheelAccumulatorRef.current = 0;
      }

      wheelAccumulatorRef.current += deltaY;

      if (direction > 0 && wheelAccumulatorRef.current >= 42) {
        return stepSequence(1);
      }

      if (direction < 0 && wheelAccumulatorRef.current <= -42) {
        return stepSequence(-1);
      }

      return true;
    };

    const handleWheel = (event) => {
      if (!consumeDelta(event.deltaY)) return;
      event.preventDefault();
    };

    const handleKeyDown = (event) => {
      const forwardKeys = ['ArrowDown', 'PageDown', ' ', 'Spacebar'];
      const backwardKeys = ['ArrowUp', 'PageUp'];

      if (forwardKeys.includes(event.key)) {
        if (!consumeDelta(60)) return;
        event.preventDefault();
      } else if (backwardKeys.includes(event.key)) {
        if (!consumeDelta(-60)) return;
        event.preventDefault();
      }
    };

    const handleTouchStart = (event) => {
      touchStartRef.current = event.touches[0]?.clientY ?? null;
    };

    const handleTouchMove = (event) => {
      const currentY = event.touches[0]?.clientY;
      if (touchStartRef.current == null || currentY == null) return;

      const deltaY = touchStartRef.current - currentY;
      if (!consumeDelta(deltaY)) {
        touchStartRef.current = currentY;
        return;
      }
      event.preventDefault();
      touchStartRef.current = currentY;
    };

    updateEligibility();
    window.addEventListener('scroll', updateEligibility, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      syncLockState(false);
      window.removeEventListener('scroll', updateEligibility);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [FINAL_STEP]);

  useEffect(() => {
    const sectionEl = sectionRef.current;
    if (!sectionEl) return;

    sectionEl.classList.toggle('is-sequence-locked', isLocked);

    return () => {
      sectionEl.classList.remove('is-sequence-locked');
    };
  }, [isLocked]);

  const maxRx = Math.max(sceneSize.w * 0.46, 380);
  const maxRy = Math.max(Math.min(maxRx * 0.28, sceneSize.h * 0.24), 98);
  const basinWidth = 220;
  const basinHeight = 52;
  const surfaceWidth = 332;
  const surfaceHeight = 98;
  const visibleDots = Math.min(sequenceStep, TOTAL_DOTS);
  const isClimax = sequenceStep >= TOTAL_DOTS;
  const showChevron = sequenceStep >= TOTAL_DOTS + 1;
  const showRipple = sequenceStep >= FINAL_STEP;
  const svgWidth = Math.max(sceneSize.w, 1200);
  const svgHeight = Math.max(sceneSize.h, 640);

  return (
    <>
      {showScene && (
        <div className="cta-ripple-scene" aria-hidden="true">
          <div
            className="cta-ripple-origin-glow"
            style={{
              left: `${origin.x}px`,
              top: `${origin.y}px`,
              opacity: showRipple ? 0.9 : 0,
            }}
          />
          <div
            className="cta-ripple-basin"
            style={{
              left: `${origin.x}px`,
              top: `${origin.y + 14}px`,
              width: `${basinWidth}px`,
              height: `${basinHeight}px`,
              opacity: showRipple ? 0.72 : 0,
            }}
          />
          <div
            className="cta-ripple-surface"
            style={{
              left: `${origin.x}px`,
              top: `${origin.y + 18}px`,
              width: `${surfaceWidth}px`,
              height: `${surfaceHeight}px`,
              opacity: showRipple ? 0.55 : 0,
            }}
          />

          <svg
            className="cta-ripple-svg"
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            preserveAspectRatio="none"
          >
            <g className="cta-ripple-group" style={{ opacity: showRipple ? 1 : 0 }}>
              {waves.map((wave, i) => {
                const rx = maxRx * wave.spread;
                const ry = maxRy * wave.roundness;
                const innerRx = Math.max(rx - wave.band, 24);
                const innerRy = Math.max(ry - wave.cut, 12);
                const crestRx = Math.max(rx - wave.band * 0.42, 28);
                const crestRy = Math.max(ry - wave.cut * 0.56, 14);

                return (
                  <g key={i} transform={`translate(${origin.x} ${origin.y + wave.centerY})`}>
                    <g
                      className="cta-wave"
                      style={{
                        '--wave-delay': `${wave.delay}s`,
                        '--wave-duration': `${wave.dur}s`,
                        animationPlayState: showRipple ? 'running' : 'paused',
                      }}
                    >
                      <ellipse
                        className="cta-wave-shadow"
                        cx="0"
                        cy={wave.shadowDrop}
                        rx={rx}
                        ry={ry}
                        style={{ opacity: wave.opacity * 0.52 }}
                      />
                      <ellipse
                        className="cta-wave-body"
                        cx="0"
                        cy="0"
                        rx={rx}
                        ry={ry}
                        style={{ opacity: wave.opacity * 0.78 }}
                      />
                      <ellipse
                        className="cta-wave-cut"
                        cx="0"
                        cy={wave.lift * -1}
                        rx={innerRx}
                        ry={innerRy}
                      />
                      <ellipse
                        className="cta-wave-crest"
                        cx="0"
                        cy={wave.crestLift * -1}
                        rx={crestRx}
                        ry={crestRy}
                        style={{
                          opacity: wave.opacity * 0.88,
                          strokeWidth: Math.max(wave.band * 0.22, 5),
                        }}
                      />
                    </g>
                  </g>
                );
              })}
            </g>
          </svg>

          <div className="cta-ripple-diffuse" />
        </div>
      )}

      {showGuide && (
        <div ref={sentinelRef} className="cta-ripple-guide" aria-hidden="true">
          {Array.from({ length: TOTAL_DOTS }, (_, i) => {
            const isLast = i === TOTAL_DOTS - 1;
            const active = i < visibleDots;

            return (
              <div
                key={i}
                ref={isLast ? finalDotAnchorRef : null}
                className={`ripple-dot ${isLast ? 'is-last' : ''} ${isLast && isClimax ? 'is-climax' : ''}`}
                style={{
                  opacity: active ? 1 : 0.06,
                  transform: active
                    ? `translateY(0) scale(${isLast && isClimax ? 1.14 : 1})`
                    : 'translateY(-6px) scale(0.82)',
                }}
              >
                <span
                  className="ripple-dot-core"
                  style={{
                    transform: `scale(${isLast && isClimax ? 1.18 : 1})`,
                    background: isLast ? '#22C55E' : active ? '#22C55E' : 'rgba(34, 197, 94, 0.14)',
                    boxShadow: isLast
                      ? isClimax
                        ? '0 0 0 6px rgba(34, 197, 94, 0.16), 0 0 18px rgba(34, 197, 94, 0.42)'
                        : active
                          ? '0 0 10px rgba(34, 197, 94, 0.24)'
                          : 'none'
                      : active
                        ? '0 0 8px rgba(34, 197, 94, 0.28)'
                        : 'none',
                  }}
                />
              </div>
            );
          })}

          <svg
            className={`ripple-chevron ${showChevron ? 'is-visible' : ''}`}
            width="28"
            height="16"
            viewBox="0 0 28 16"
            style={{
              opacity: showChevron ? 1 : 0,
              transform: showChevron ? 'translateY(0)' : 'translateY(-4px)',
            }}
          >
            <polyline
              points="3,4 14,13 25,4"
              fill="none"
              stroke="#22C55E"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </>
  );
}

function TickEtcMinimal() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [deckOrder, setDeckOrder] = useState(['ticketing', 'seatmaps', 'payments', 'dashboard']);
  const activeTab = deckOrder[0];
  const [navSurface, setNavSurface] = useState('dark');
  const heroMockupRef = useRef(null);
  const howItWorksRef = useRef(null);
  const pricingSectionRef = useRef(null);
  const ctaStructureRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [replayProgress, setReplayProgress] = useState(0);
  const [pricingParallaxY, setPricingParallaxY] = useState(0);
  const showCtaRipple = true;
  const [ctaSequenceStep, setCtaSequenceStep] = useState(0);
  const clamp01 = (value) => Math.max(0, Math.min(1, value));
  const CTA_RUN_START_STEP = 0;
  const CTA_RUN_END_STEP = 7;
  const CTA_CONTROL_START_STEP = 5;
  const CTA_CONTROL_END_STEP = 9;
  const ctaRunProgress = clamp01((ctaSequenceStep - CTA_RUN_START_STEP) / (CTA_RUN_END_STEP - CTA_RUN_START_STEP));
  const ctaControlProgress = clamp01((ctaSequenceStep - CTA_CONTROL_START_STEP) / (CTA_CONTROL_END_STEP - CTA_CONTROL_START_STEP));
  const ctaRunTranslateX = `${(1 - ctaRunProgress) * 118}vw`;
  const ctaRunBlur = `${(1 - ctaRunProgress) * 12}px`;
  const ctaControlTranslateY = `${(1 - ctaControlProgress) * 14}px`;
  const ctaControlBlur = `${(1 - ctaControlProgress) * 8}px`;

  const bringTabToFront = useCallback((tab) => {
    setDeckOrder((currentOrder) => {
      const index = currentOrder.indexOf(tab);
      if (index <= 0) return currentOrder;
      return [
        ...currentOrder.slice(index),
        ...currentOrder.slice(0, index),
      ];
    });
  }, []);

  // Mouse parallax effect for hero mockup
  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth - 0.5) * 20;
      const y = (clientY / innerHeight - 0.5) * 20;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Scroll-driven progress for How We Work section
  useEffect(() => {
    const handleScroll = () => {
      const el = howItWorksRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const totalScrollLength = el.offsetHeight - window.innerHeight;
      const scrolled = -rect.top;
      const totalProgress = totalScrollLength > 0 ? scrolled / totalScrollLength : 0;
      const revealSpan = 0.78;
      const replaySpan = 0.22;
      const progress = Math.min(Math.max(totalProgress / revealSpan, 0), 1);
      const replay = Math.min(Math.max((totalProgress - revealSpan) / replaySpan, 0), 1);
      setScrollProgress(progress);
      setReplayProgress(replay);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Stronger foreground/background separation for the pricing section
  useEffect(() => {
    const handlePricingParallax = () => {
      const el = pricingSectionRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const viewportCenter = window.innerHeight * 0.5;
      const sectionCenter = rect.top + rect.height * 0.5;
      const distanceFromCenter = viewportCenter - sectionCenter;
      const shift = Math.max(-90, Math.min(90, distanceFromCenter * 0.14));

      setPricingParallaxY(shift);
    };

    handlePricingParallax();
    window.addEventListener('scroll', handlePricingParallax, { passive: true });
    window.addEventListener('resize', handlePricingParallax);

    return () => {
      window.removeEventListener('scroll', handlePricingParallax);
      window.removeEventListener('resize', handlePricingParallax);
    };
  }, []);

  useEffect(() => {
    const updateNavSurface = () => {
      const sampleX = window.innerWidth / 2;
      const sampleY = 74;
      const surface = document
        .elementsFromPoint(sampleX, sampleY)
        .map((element) => element.closest?.('[data-nav-surface]'))
        .find(Boolean);

      setNavSurface(surface?.dataset.navSurface === 'light' ? 'light' : 'dark');
    };

    updateNavSurface();
    window.addEventListener('scroll', updateNavSurface, { passive: true });
    window.addEventListener('resize', updateNavSurface);

    return () => {
      window.removeEventListener('scroll', updateNavSurface);
      window.removeEventListener('resize', updateNavSurface);
    };
  }, []);

  const valueProps = [
    { icon: '🎪', title: 'Festivals', desc: 'Multi-day events with camping' },
    { icon: '🏢', title: 'Corporate Events', desc: 'Conferences & team building' },
    { icon: '🏛️', title: 'Venues', desc: 'Theaters & concert halls' },
    { icon: '⚽', title: 'Sport Clubs', desc: 'Matches & season tickets' },
    { icon: '🎯', title: 'Conferences', desc: 'Professional gatherings' },
    { icon: '🎵', title: 'Club Nights', desc: 'Recurring events & memberships' }
  ];

  const steps = [
    {
      number: '01',
      title: 'Create your event',
      desc: 'Set up tickets, pricing & capacity in minutes'
    },
    {
      number: '02',
      title: 'Start selling instantly',
      desc: 'Share your link and accept payments'
    },
    {
      number: '03',
      title: 'Get paid automatically',
      desc: 'Real-time settlements to your account'
    }
  ];

  const productTabs = {
    ticketing: {
      title: 'SMART TICKETING',
      icon: 'ticket',
      desc: 'Sell any ticket type, any event, fully branded.',
      features: ['Unlimited events', 'Multiple ticket types', 'Early bird / phases', 'Discount codes']
    },
    payments: {
      title: 'REAL-TIME PAYMENTS',
      icon: 'payment',
      desc: 'Instant settlements, split payments, zero surprises.',
      features: ['Instant split payments', 'Transparent fees', 'Multiple PSP support', 'Auto reconciliation']
    },
    seatmaps: {
      title: 'SEAT MAPS & ACCREDITATION',
      icon: 'seatmap',
      desc: 'Interactive seating and full access control.',
      features: ['Interactive seating', 'Access control', 'Guestlist & accreditation', 'Digital badges']
    },
    dashboard: {
      title: 'LIVE DASHBOARD',
      icon: 'dashboard',
      desc: 'Real-time insights across all your events.',
      features: ['Real-time sales', 'Revenue insights', 'Exportable reports', 'Multi-user access']
    }
  };

  const whyTickEtc = [
    'More control than traditional platforms',
    'Better margins',
    'Transparent processor structure',
    'Dedicated support'
  ];

  const integrations = [
    'POS Systems',
    'Mobile Scanning',
    'API Connections',
    'CRM Integration',
    'Affiliate Tracking'
  ];

  const floatingTickets = useMemo(() => {
    // kleine, snelle deterministic PRNG
    const mulberry32 = (seed) => {
      let a = seed >>> 0;
      return () => {
        a |= 0;
        a = (a + 0x6D2B79F5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    };

    return Array.from({ length: 18 }, (_, i) => {
      const rnd = mulberry32(i + 1); // per ticket vaste random stream
      const r1 = rnd();
      const r2 = rnd();
      const r3 = rnd();
      const r4 = rnd();

      return {
        id: i,
        left: `${Math.round(r1 * 96 + 2)}%`,  // 2..98% (full width spread)
        baseRotate: r2 * 40 - 20,             // -20..20 deg
        depth: 0.4 + r3 * 1.2,                // 0.4..1.6 (parallax strength)
        delay: `${i * 0.55}s`,
        duration: `${15 + r4 * 10}s`,         // 15..25s, maar stabiel
      };
    });
  }, []);

  return (
    <div className="tick-etc-minimal">
      {/* Navigation */}
      <nav className={`minimal-nav ${navSurface === 'light' ? 'minimal-nav--smokey' : ''}`}>
        <div className="nav-container">
          <div className="nav-links nav-links--left">
            <a href="#product">Request Info</a>
            <a href="#demo">Book Demo</a>
            <span className="nav-brand-word">TICK-ETC.</span>
          </div>
          <div className="nav-logo" aria-label="&amp;c. logo">
            <img src="/images/tick-etc%20logo%20blue%20gradient.png" alt="" className="nav-logo-image nav-logo-image--default" />
            <img src="/images/tick-etc%20logo%20on%20hover.png" alt="" className="nav-logo-image nav-logo-image--hover" />
          </div>
          <div className="nav-links nav-links--right">
            <a href="#login" className="nav-cta nav-cta--login">Login</a>
            <a href="#ticket-lost" className="nav-cta nav-cta--blue">Ticket Lost?</a>
          </div>
        </div>
      </nav>

      {/* Hero Section - Fullscreen 100vh */}
      <div className="hero-flow" data-nav-surface="dark">
        <div className="floating-tickets-bg">
          {floatingTickets.map((t) => (
            <div
              key={t.id}
              className="ticket-float"
              style={{
                left: t.left,
                animationDelay: t.delay,
                animationDuration: t.duration
              }}
            >
              <div
                className="ticket-parallax"
                style={{
                  transform: `translate3d(${mousePosition.x * t.depth * 2.5}px, ${mousePosition.y * t.depth * 2.5}px, 0) rotate(${t.baseRotate}deg)`
                }}
              >
                <img src="/TICKET.png" alt="" className="ticket-img" />
              </div>
            </div>
          ))}
        </div>
        <section className="hero-minimal">

          <div className="hero-container">
            <div className="hero-gradient-rule" aria-hidden="true">
              <span className="hero-rule-caption hero-rule-caption--left">TICKETING, ACCESS &amp; E-COMMERCE</span>
            </div>
            <span className="hero-brand-accent" aria-hidden="true">Tick-etc.</span>
            <div className="hero-left">
              <h1 className="hero-headline">
                No Ticket Fees.
                <br />
                <span className="hero-keep-text">Keep </span>
                <span className="accent-text hero-control-text">Control.</span>
              </h1>
              <p className="hero-subline">
                Professional event ticketing software with split payments,
                advanced marketing options and full event revenue control.
              </p>
              <div className="hero-actions">
                <button className="btn-primary">Start Selling</button>
                <button className="btn-secondary">Book Demo</button>
              </div>
              <p className="trust-line">Trusted by festivals, venues, sport & events.</p>
            </div>

            <div
  className="hero-right"
  style={{
    opacity: 1,
    animation: 'none',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '40px',
    width: '100%',
    maxWidth: '600px',
    minWidth: 0,
  }}
>
  <span className="hero-data-driven">DATA DRIVEN</span>
  <div style={{
    flex: '0 0 auto',
    width: 'max-content',
    contain: 'layout',
    transform: 'scale(0.605)',
    transformOrigin: 'center center',
    marginLeft: '-90px',
    marginRight: '-90px',
  }}>
    <AnimatedMockup mousePosition={mousePosition} />
  </div>
  <div style={{
    flex: '0 0 auto',
    width: 'max-content',
    transform: 'scale(0.605)',
    transformOrigin: 'center center',
    marginLeft: '-80px',
    marginRight: '-80px',
  }}>
    <MobileAnalyticsDashboard />
  </div>
</div>
          </div>
          {/* Scroll Indicator with Gradient Line */}
          <div className="scroll-indicator hero-scroll-indicator">
            <span className="scroll-text">Scroll to explore</span>
            <div className="scroll-line-gradient"></div>
          </div>
        </section>

        {/* How We Work — scroll-driven pinned sectie */}
        <div className="hiw-outer" ref={howItWorksRef}>
          <div className="hiw-sticky">
            {/* Parallax foto — beweegt langzamer dan content */}
            <div
              className="hiw-bg-photo"
              style={{
                transform: `translateY(${scrollProgress * -80}px)`,
              }}
            >
              <img src="/event_showcase2.jpg" alt="" className="hiw-bg-img" />
            </div>
            <h2 className="hiw-title">
              How <span className="accent-text">We</span> Work
            </h2>

            {/* Timeline line met genummerde dots */}
            <div className="hiw-line-track">
              <div
                className="hiw-line-fill"
                style={{ width: scrollProgress > 0.15 ? `${Math.min((scrollProgress - 0.15) / 0.35 * 100, 100)}%` : '0%' }}
              />
              {steps.map((step, index) => {
                const threshold = 0.35 + index * 0.2;
                const visible = scrollProgress >= threshold;
                // Dot positie: 16.5%, 50%, 83.5% (gecentreerd boven elke card)
                const positions = ['16.5%', '50%', '83.5%'];
                return (
                  <div
                    key={index}
                    className={`hiw-dot ${visible ? 'hiw-dot--visible' : ''}`}
                    style={{ left: positions[index] }}
                  >
                    {index + 1}
                  </div>
                );
              })}
            </div>

            {/* Steps — zonder nummer */}
            <div className="hiw-steps">
              {steps.map((step, index) => {
                const threshold = 0.35 + index * 0.2;
                const visible = scrollProgress >= threshold;
                const replayStart = index * 0.28;
                const replayStrength = Math.min(Math.max((replayProgress - replayStart) / 0.18, 0), 1);
                return (
                  <div
                    key={index}
                    className={`hiw-step ${visible ? 'hiw-step--visible' : ''} ${replayStrength > 0 ? 'hiw-step--replay-active' : ''}`}
                    style={{
                      '--hiw-replay-y': `${replayStrength * -8}px`,
                      '--hiw-replay-scale': `${1 + replayStrength * 0.018}`,
                    }}
                  >
                    <div className="hiw-step-content">
                      <h3>{step.title}</h3>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Product Showcase - Split Screen Light */}
      <section id="product" className="ps-section" data-nav-surface="light">
        <div className="ps-inner">

          {/* Left: vertical sidebar */}
          <div className="ps-sidebar">
            <p className="ps-eyebrow">PLATFORM</p>
            <h2 className="ps-heading">Everything you need.<br />Nothing you don't.</h2>
            <nav className="ps-nav">
              {Object.keys(productTabs).map((tab) => (
                <button
                  key={tab}
                  className={`ps-nav-item ${activeTab === tab ? 'ps-nav-item--active' : ''}`}
                  onClick={() => bringTabToFront(tab)}
                >
                  <span className="ps-nav-icon">
                    <PsIcon name={productTabs[tab].icon} />
                  </span>
                  <span className="ps-nav-label">{productTabs[tab].title}</span>
                  <span className="ps-nav-arrow">→</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Right: colored depth folder tabs */}
          <PlatformFolderStack
            productTabs={productTabs}
            deckOrder={deckOrder}
            activeTab={activeTab}
            setActiveTab={bringTabToFront}
          />
        </div>
      </section>

      {/* Seated showcase from react.zip */}
      <div data-nav-surface="light">
        <SeatedShowcase />
      </div>

      {/* Fee Philosophy 
      <section className="fee-philosophy">
        <div className="container">
          <div className="glass-card">
            <h2>Transparent Fee Philosophy</h2>
            <div className="fee-points">
              <div className="fee-point">
                <span className="fee-check">✓</span>
                <p>Clear transaction fees</p>
              </div>
              <div className="fee-point">
                <span className="fee-check">✓</span>
                <p>No hidden revenue share</p>
              </div>
              <div className="fee-point">
                <span className="fee-check">✓</span>
                <p>Structured payment architecture</p>
              </div>
            </div>
          </div>
        </div>
      </section>*/}

      <div className="seamless-dark-flow" data-nav-surface="dark">
        {/* Pricing */}
        <section id="pricing" className="pricing-section" ref={pricingSectionRef}>
          <div
            className="pricing-background"
            aria-hidden="true"
            style={{
              transform: `translate3d(0, ${pricingParallaxY}px, 0) scale(1.16)`,
            }}
          >
            <img
              src="/images/background%20structure.JPEG"
              alt=""
              className="pricing-background-image"
            />
            <div className="pricing-background-overlay" />
          </div>
          <div className="container pricing-journey-container">
            <div className="pricing-journey-copy">
              <h2 className="section-title">
                No Ticket Fees.
                <span className="accent-text">Transparent Structure.</span>
              </h2>
              <p className="pricing-subline">
                Instead of charging ticket fees, Tick-etc. uses a structured payment model that keeps pricing transparent for organizers, venues, and clubs.
              </p>
            </div>
            <JourneyCards />
          </div>
        </section>

        {/* Why Switch */}
        <WhySection />

        {/* Integrations */}
        <IntegrationsHub />
      </div>

      <div className="seamless-light-flow" data-nav-surface="light">
        {/* Value Strip - Social Proof */}
        <SlotMachineStrip />

        {/* Final CTA */}
        <section className="final-cta">
          {/* Scroll-triggered dotted line + full-section water ripple */}
          {showCtaRipple && (
            <RippleTransition
              rippleTargetRef={ctaStructureRef}
              showScene={false}
              showGuide
              onSequenceStepChange={setCtaSequenceStep}
            />
          )}
          <div className="cta-container">
            <div className="cta-console">
              <div className="cta-console-top">
                <span>Tick-etc. launch</span>
                <span>Get ready</span>
              </div>
              <h2 className="cta-headline">
                <span
                  className="cta-headline-line cta-headline-line--run"
                  style={{
                    transform: `translate3d(${ctaRunTranslateX}, 0, 0)`,
                    opacity: ctaRunProgress,
                    filter: `blur(${ctaRunBlur})`,
                  }}
                >
                  Ready to run your event
                </span>
                <span
                  ref={ctaStructureRef}
                  className="cta-headline-line cta-headline-line--control cta-solid-word"
                  style={{
                    transform: `translate3d(0, ${ctaControlTranslateY}, 0)`,
                    opacity: ctaControlProgress,
                    filter: `blur(${ctaControlBlur})`,
                  }}
                >
                  without losing control?
                </span>
              </h2>
              <p className="cta-subcopy">
                Structure ticketing, payments, access and settlement from one premium operating flow.
              </p>
              <div className="cta-actions">
                <button className="btn-cta-large">Book Demo</button>
                <button className="btn-cta-secondary">View Structure</button>
              </div>
              <div className="cta-signal-row" aria-label="Platform readiness">
                <span>Online Shop</span>
                <span>Ticketing</span>
                <span>Payments</span>
                <span>Access</span>
                <span>Settlements</span>
                <span>Data</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="minimal-footer">
        <div className="footer-container">
          <div className="footer-columns">
            <div className="footer-col">
              <div className="footer-logo">Tick-etc.</div>
              <p>Professional ticketing software</p>
            </div>

            <div className="footer-col">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#api">API</a>
            </div>

            <div className="footer-col">
              <h4>Company</h4>
              <a href="#about">About</a>
              <a href="#contact">Contact</a>
              <a href="#careers">Careers</a>
            </div>

            <div className="footer-col">
              <h4>Legal</h4>
              <a href="#privacy">Privacy</a>
              <a href="#terms">Terms</a>
              <a href="#security">Security</a>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2026 We-Sell BV. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default TickEtcMinimal;
